<template>
    <div class="min-h-screen bg-gray-50/50">
        <div class="mx-auto max-w-7xl space-y-5 px-4 py-8 sm:px-6 lg:px-8">
            <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 class="text-xl font-semibold text-gray-800">
                        {{ t("vue.kiosk_ai_spend") }}
                    </h1>
                    <p class="text-sm text-gray-500">
                        {{ range.from }} → {{ range.to }}
                    </p>
                </div>

                <div
                    class="inline-flex rounded-lg border border-gray-200 bg-white p-0.5"
                >
                    <inertia-link
                        v-for="p in periods"
                        :key="p"
                        :href="periodHref(p)"
                        preserve-scroll
                        class="rounded-md px-3 py-1.5 text-sm font-medium capitalize transition"
                        :class="
                            p === period
                                ? 'bg-gray-800 text-white'
                                : 'text-gray-500 hover:bg-gray-50'
                        "
                    >
                        {{ t(`vue.kiosk_ai_spend_period_${p}`) }}
                    </inertia-link>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div class="rounded-xl border border-gray-200 bg-white p-5">
                    <p
                        class="text-xs font-medium uppercase tracking-wide text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_spend_total") }}
                    </p>
                    <p class="mt-1 text-2xl font-semibold text-gray-800">
                        {{ formatUsd(spend.total_usd) }}
                    </p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-white p-5">
                    <p
                        class="text-xs font-medium uppercase tracking-wide text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_spend_priced_messages") }}
                    </p>
                    <p class="mt-1 text-2xl font-semibold text-gray-800">
                        {{
                            new Intl.NumberFormat().format(
                                spend.priced_message_count,
                            )
                        }}
                    </p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-white p-5">
                    <p
                        class="text-xs font-medium uppercase tracking-wide text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_spend_all_messages") }}
                    </p>
                    <p class="mt-1 text-2xl font-semibold text-gray-800">
                        {{
                            new Intl.NumberFormat().format(spend.message_count)
                        }}
                    </p>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div
                    class="overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                    <h2
                        class="border-b border-gray-100 px-5 py-3 text-sm font-semibold text-gray-700"
                    >
                        {{ t("vue.kiosk_ai_spend_by_team") }}
                    </h2>
                    <p
                        v-if="spend.by_team.length === 0"
                        class="px-5 py-4 text-sm text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_spend_empty") }}
                    </p>
                    <table v-else class="w-full text-sm">
                        <tbody>
                            <tr
                                v-for="team in spend.by_team"
                                :key="team.team_id ?? 'unknown'"
                                class="border-b border-gray-50 last:border-0"
                            >
                                <td class="px-5 py-2 text-gray-700">
                                    {{ team.team_name ?? `#${team.team_id}` }}
                                </td>
                                <td class="px-5 py-2 text-right text-gray-400">
                                    {{
                                        new Intl.NumberFormat().format(
                                            team.message_count,
                                        )
                                    }}
                                </td>
                                <td
                                    class="px-5 py-2 text-right font-medium text-gray-800"
                                >
                                    {{ formatUsd(team.total_usd) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div
                    class="overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                    <h2
                        class="border-b border-gray-100 px-5 py-3 text-sm font-semibold text-gray-700"
                    >
                        {{ t("vue.kiosk_ai_spend_by_model") }}
                    </h2>
                    <p
                        v-if="spend.by_model.length === 0"
                        class="px-5 py-4 text-sm text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_spend_empty") }}
                    </p>
                    <table v-else class="w-full text-sm">
                        <tbody>
                            <tr
                                v-for="model in spend.by_model"
                                :key="model.model"
                                class="border-b border-gray-50 last:border-0"
                            >
                                <td
                                    class="px-5 py-2 font-mono text-xs text-gray-700"
                                >
                                    {{ model.model }}
                                </td>
                                <td class="px-5 py-2 text-right text-gray-400">
                                    {{
                                        new Intl.NumberFormat().format(
                                            model.message_count,
                                        )
                                    }}
                                </td>
                                <td
                                    class="px-5 py-2 text-right font-medium text-gray-800"
                                >
                                    {{ formatUsd(model.total_usd) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div
                class="overflow-hidden rounded-xl border border-gray-200 bg-white"
            >
                <div
                    class="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-3"
                >
                    <h2 class="text-sm font-semibold text-gray-700">
                        {{ t("vue.kiosk_ai_activity") }}
                    </h2>

                    <div class="flex flex-wrap items-center gap-3">
                        <p
                            v-if="activity.truncated"
                            class="text-xs text-gray-400"
                        >
                            {{
                                t("vue.kiosk_ai_activity_truncated", {
                                    shown: activity.rows.length,
                                    total: activity.total_count,
                                })
                            }}
                        </p>
                        <input
                            v-model="search"
                            type="search"
                            :placeholder="t('vue.kiosk_ai_activity_search')"
                            class="w-56 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-gray-400 focus:outline-hidden"
                        />
                    </div>
                </div>

                <p
                    v-if="activity.rows.length === 0"
                    class="px-5 py-4 text-sm text-gray-400"
                >
                    {{ t("vue.kiosk_ai_spend_empty") }}
                </p>

                <div v-else class="overflow-x-auto">
                    <table class="w-full text-sm">
                        <thead>
                            <tr class="border-b border-gray-100 text-left">
                                <th
                                    v-for="column in columns"
                                    :key="column.key"
                                    class="px-5 py-2 text-xs font-medium uppercase tracking-wide text-gray-400"
                                    :class="column.numeric ? 'text-right' : ''"
                                >
                                    <button
                                        type="button"
                                        class="inline-flex cursor-pointer items-center gap-1 uppercase transition hover:text-gray-600"
                                        :class="
                                            activity.sort === column.key
                                                ? 'text-gray-700'
                                                : ''
                                        "
                                        @click="applySort(column.key)"
                                    >
                                        {{ t(column.label) }}
                                        <span
                                            v-if="activity.sort === column.key"
                                            aria-hidden="true"
                                            >{{
                                                activity.direction === "asc"
                                                    ? "↑"
                                                    : "↓"
                                            }}</span
                                        >
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="row in activity.rows"
                                :key="row.chat_id"
                                class="border-b border-gray-50 last:border-0"
                            >
                                <td class="max-w-xs px-5 py-2 text-gray-700">
                                    <span class="block truncate">
                                        {{ row.title ?? `#${row.chat_id}` }}
                                    </span>
                                </td>
                                <td class="px-5 py-2">
                                    <inertia-link
                                        :href="
                                            route(
                                                'kiosk.teams.ai-usage.index',
                                                { team: row.team_id },
                                            )
                                        "
                                        class="text-gray-700 underline-offset-2 hover:text-gray-900 hover:underline"
                                    >
                                        {{ row.team_name ?? `#${row.team_id}` }}
                                    </inertia-link>
                                </td>
                                <td class="px-5 py-2">
                                    <inertia-link
                                        :href="
                                            route(
                                                'kiosk.users.ai-usage.index',
                                                { user: row.user_id },
                                            )
                                        "
                                        class="text-gray-700 underline-offset-2 hover:text-gray-900 hover:underline"
                                    >
                                        {{
                                            row.user_email ?? `#${row.user_id}`
                                        }}
                                    </inertia-link>
                                </td>
                                <td
                                    class="px-5 py-2 text-right font-medium text-gray-800"
                                >
                                    {{
                                        new Intl.NumberFormat().format(
                                            row.message_count,
                                        )
                                    }}
                                </td>
                                <td class="px-5 py-2 text-right text-gray-500">
                                    {{
                                        new Intl.NumberFormat().format(
                                            row.tool_call_count,
                                        )
                                    }}
                                </td>
                                <td
                                    class="whitespace-nowrap px-5 py-2 text-gray-400"
                                >
                                    {{ row.created_at }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="h-[200px]" aria-hidden="true"></div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";

interface ITeamSpend {
    team_id: number | null;
    team_name: string | null;
    total_usd: number;
    message_count: number;
}

interface IModelSpend {
    model: string;
    total_usd: number;
    message_count: number;
}

interface IActivityRow {
    chat_id: number;
    title: string | null;
    team_id: number;
    team_name: string | null;
    user_id: number;
    user_email: string | null;
    message_count: number;
    tool_call_count: number;
    created_at: string;
    last_message_at: string | null;
}

type ActivitySortKey =
    | "chat"
    | "team"
    | "user"
    | "messages"
    | "tool_calls"
    | "created";

interface IActivityColumn {
    key: ActivitySortKey;
    label: string;
    numeric: boolean;
}

const props = defineProps<{
    period: string;
    periods: string[];
    range: { from: string; to: string };
    spend: {
        total_usd: number;
        message_count: number;
        priced_message_count: number;
        by_team: ITeamSpend[];
        by_model: IModelSpend[];
    };
    activity: {
        rows: IActivityRow[];
        total_count: number;
        limit: number;
        truncated: boolean;
        search: string | null;
        sort: ActivitySortKey;
        direction: "asc" | "desc";
    };
}>();

const { t } = useTranslations();

const formatUsd = (value: number): string =>
    `$${value.toFixed(value < 0.01 && value > 0 ? 4 : 2)}`;

const columns: IActivityColumn[] = [
    { key: "chat", label: "vue.kiosk_ai_activity_chat", numeric: false },
    { key: "team", label: "vue.kiosk_ai_activity_team", numeric: false },
    { key: "user", label: "vue.kiosk_ai_activity_user", numeric: false },
    {
        key: "messages",
        label: "vue.kiosk_ai_activity_messages",
        numeric: true,
    },
    { key: "tool_calls", label: "vue.kiosk_ai_activity_tools", numeric: true },
    { key: "created", label: "vue.kiosk_ai_activity_created", numeric: false },
];

const search = ref<string>(props.activity.search ?? "");

// Switching the period keeps the table's own search and ordering.
const periodHref = (period: string): string =>
    route("kiosk.ai-spend.index", {
        period,
        search: props.activity.search ?? "",
        sort: props.activity.sort,
        direction: props.activity.direction,
    });

const reload = (params: {
    search?: string | null;
    sort?: ActivitySortKey;
    direction?: "asc" | "desc";
}): void => {
    router.get(
        route("kiosk.ai-spend.index"),
        {
            period: props.period,
            search: params.search ?? search.value ?? "",
            sort: params.sort ?? props.activity.sort,
            direction: params.direction ?? props.activity.direction,
        },
        { preserveState: true, preserveScroll: true, replace: true },
    );
};

// Clicking the active column flips the direction; a new column starts descending.
const applySort = (key: ActivitySortKey): void => {
    reload({
        sort: key,
        direction:
            props.activity.sort === key && props.activity.direction === "desc"
                ? "asc"
                : "desc",
    });
};

let searchTimer: ReturnType<typeof setTimeout> | null = null;

watch(search, (value: string): void => {
    if (searchTimer !== null) {
        clearTimeout(searchTimer);
    }

    searchTimer = setTimeout(() => reload({ search: value }), 350);
});
</script>
